import Container from "@@/elements/Container";
import { useEffect, useState, useRef } from "react";
import CookieConsentComponent from "./cookieConsent";

type FormProps = {
  backgroundColor: "beige" | "white";
  header: string;
  messageText: string;
  linkLabel: string;
  form: any;
  hasConsent: boolean;
  overrideCookieConsent: boolean;
  anchorId?: string;
};

type CookieConsentObject = {
  stamp: string;
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
  method: string;
};

const EmbeddedForm = ({
  backgroundColor,
  header,
  messageText,
  linkLabel,
  form,
  hasConsent,
  overrideCookieConsent,
  anchorId,
}: FormProps) => {
  const [marketingCookie, setMarketingCookie] = useState<boolean>(hasConsent);
  const prevFormRef = useRef<string | null>(null);
  const formContainerRef = useRef<HTMLDivElement>(null);

  const cookieBotAcceptHandler = (e) => {
    const cookie = (e.target as any)?.CookieConsent
      ?.consent as CookieConsentObject;
    if (cookie === null || cookie === undefined || !cookie?.marketing) {
      setMarketingCookie(false);
      return;
    }
    setMarketingCookie(true);
  };

  useEffect(() => {
    addEventListener("CookiebotOnAccept", cookieBotAcceptHandler);
    addEventListener("CookiebotOnDecline", cookieBotAcceptHandler);
    
    return () => {
      removeEventListener("CookiebotOnAccept", cookieBotAcceptHandler);
      removeEventListener("CookiebotOnDecline", cookieBotAcceptHandler);
    };
  }, []);

  const getbackgroundColor = () => {
    if (backgroundColor === "beige") {
      return "ComplementaryBeigeTint03";
    } else return "White";
  };

  const shouldRenderForm = (marketingCookie || overrideCookieConsent);

  useEffect(() => {
    if (!shouldRenderForm || !form || !formContainerRef.current) return;
    if (prevFormRef.current === form) return;
    prevFormRef.current = form;

    const container = formContainerRef.current;
    const scriptRegex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
    const scripts = form.match(scriptRegex) || [];

    const externalScripts: string[] = [];
    const inlineScripts: string[] = [];

    scripts.forEach((scriptTag) => {
      const srcMatch = scriptTag.match(/src\s*=\s*["'](.*?)["']/);
      if (srcMatch) {
        externalScripts.push(srcMatch[1]);
      } else {
        const content = scriptTag.replace(/<script[^>]*>|<\/script>/gi, "").trim();
        if (content) inlineScripts.push(content);
      }
    });

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) { resolve(); return; }
        const el = document.createElement("script");
        el.src = src;
        el.onload = () => resolve();
        el.onerror = () => { console.error(`Failed to load script: ${src}`); resolve(); };
        container.appendChild(el);
      });
    };

    externalScripts.reduce(
      (chain, src) => chain.then(() => loadScript(src)),
      Promise.resolve()
    ).then(() => {
      inlineScripts.forEach((content) => {
        const scriptEl = document.createElement("script");
        scriptEl.textContent = content;
        container.appendChild(scriptEl);
      });
    });
  }, [shouldRenderForm, form]);

  // Remove scripts from HTML for rendering (we handle them separately)
  const htmlWithoutScripts = form ? form.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') : '';

  return (
    <>
      <Container
        className={`embedded-form-container ${backgroundColor}`}
        backgroundColor={getbackgroundColor()}
        pageGutter={0}
      >
        {shouldRenderForm && (
          <div ref={formContainerRef} dangerouslySetInnerHTML={{ __html: htmlWithoutScripts }} />
        )}
        {!marketingCookie && !overrideCookieConsent && (
          <CookieConsentComponent
            header={header}
            body={messageText}
            buttonLabel={linkLabel}
            backgroundColor={
              backgroundColor === "white" ? "PrimaryBlueTint03" : "White"
            }
          />
        )}
      </Container>
    </>
  );
};

export default EmbeddedForm;
